import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { 
  MagnifyingGlassIcon,
  HomeIcon,
  ChevronRightIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

const HelpLayout = ({ children, title, breadcrumbs = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const location = useLocation();

  const isPrivilegedUser = user && (
    user.role === 'admin' || 
    user.role === 'manager' || 
    user.role === 'partner' || 
    user.role === 'institution' || 
    user.is_staff
  );

  const isAdminSection = location.pathname.includes('/help/admin/');

  const userNavigation = [
    { name: 'Getting Started', href: '/help/getting-started' },
    { name: 'Courses', href: '/help/courses' },
    { name: 'Applications', href: '/help/applications' },
    { name: 'Opportunities', href: '/help/opportunities' },
    { name: 'Career Guidance', href: '/help/career-guidance' },
    { name: 'Profile', href: '/help/profile' },
    { name: 'Dashboard', href: '/help/dashboard' },
    { name: 'FAQ', href: '/help/faq' }
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/help/admin/dashboard' },
    { name: 'Course Management', href: '/help/admin/courses' },
    { name: 'Opportunity Management', href: '/help/admin/opportunities' },
    { name: 'User Administration', href: '/help/admin/users' },
    { name: 'Student Lifecycle', href: '/help/admin/lifecycle' },
    { name: 'Announcements', href: '/help/admin/announcements' },
    { name: 'Django Admin', href: '/help/admin/django' },
    { name: 'Security', href: '/help/admin/security' }
  ];

  const currentNavigation = isAdminSection ? adminNavigation : userNavigation;

  const handleSearch = (e) => {
    e.preventDefault();
    // TODO: Implement search functionality
    console.log('Searching for:', searchQuery);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--background-gray)' }}>
      {/* Help Header */}
      <div style={{
        backgroundColor: 'var(--white)',
        borderBottom: '1px solid var(--border-gray)',
        padding: '24px 0'
      }}>
        <div className="container">
          {/* Breadcrumbs */}
          <nav style={{ marginBottom: '16px' }}>
            <ol style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: 'var(--text-gray)',
              margin: 0,
              padding: 0,
              listStyle: 'none'
            }}>
              <li>
                <Link 
                  to="/" 
                  style={{ 
                    color: 'var(--text-gray)', 
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <HomeIcon style={{ width: '16px', height: '16px' }} />
                </Link>
              </li>
              <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
              <li>
                <Link 
                  to="/help" 
                  style={{ color: 'var(--text-gray)', textDecoration: 'none' }}
                >
                  Help
                </Link>
              </li>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
                  <li>
                    {crumb.href ? (
                      <Link 
                        to={crumb.href} 
                        style={{ color: 'var(--text-gray)', textDecoration: 'none' }}
                      >
                        {crumb.name}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--primary-black)', fontWeight: '500' }}>
                        {crumb.name}
                      </span>
                    )}
                  </li>
                </React.Fragment>
              ))}
            </ol>
          </nav>

          {/* Title and Search */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            <div>
              <h1 style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <BookOpenIcon style={{ width: '32px', height: '32px', color: 'var(--primary-yellow)' }} />
                {title}
              </h1>
              {isAdminSection && (
                <p style={{
                  color: 'var(--text-gray)',
                  fontSize: '14px',
                  margin: '8px 0 0 44px'
                }}>
                  Administrative Documentation
                </p>
              )}
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} style={{ position: 'relative', minWidth: '300px' }}>
              <input
                type="text"
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 44px',
                  border: '1px solid var(--border-gray)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'var(--white)',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-yellow)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-gray)'}
              />
              <MagnifyingGlassIcon style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: 'var(--text-gray)'
              }} />
            </form>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '32px 0' }}>
        <div className="help-layout">
          {/* Sidebar Navigation */}
          <div style={{
            position: 'sticky',
            top: '32px'
          }}>
            {/* Section Toggle */}
            {isPrivilegedUser && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  display: 'flex',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '8px',
                  padding: '4px'
                }}>
                  <Link
                    to="/help/getting-started"
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '500',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      backgroundColor: !isAdminSection ? 'var(--white)' : 'transparent',
                      color: !isAdminSection ? 'var(--primary-black)' : 'var(--text-gray)',
                      boxShadow: !isAdminSection ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    User Guide
                  </Link>
                  <Link
                    to="/help/admin/dashboard"
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '500',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      backgroundColor: isAdminSection ? 'var(--white)' : 'transparent',
                      color: isAdminSection ? 'var(--primary-black)' : 'var(--text-gray)',
                      boxShadow: isAdminSection ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Admin Guide
                  </Link>
                </div>
              </div>
            )}

            {/* Navigation Menu */}
            <nav className="card">
              <div className="card-body" style={{ padding: '16px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: 'var(--primary-black)',
                  marginBottom: '16px'
                }}>
                  {isAdminSection ? 'Admin Topics' : 'Help Topics'}
                </h3>
                <ul style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  {currentNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          style={{
                            display: 'block',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: isActive ? '600' : '500',
                            color: isActive ? 'var(--primary-yellow-dark)' : 'var(--text-gray)',
                            backgroundColor: isActive ? 'var(--primary-yellow-light)' : 'transparent',
                            textDecoration: 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.target.style.backgroundColor = 'var(--background-gray)';
                              e.target.style.color = 'var(--primary-black)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = 'var(--text-gray)';
                            }
                          }}
                        >
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </nav>

            {/* Quick Links */}
            <div className="card" style={{ marginTop: '24px' }}>
              <div className="card-body" style={{ padding: '16px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: 'var(--primary-black)',
                  marginBottom: '16px'
                }}>
                  Quick Links
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Link
                    to="/help"
                    style={{
                      fontSize: '14px',
                      color: 'var(--primary-yellow)',
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                  >
                    ← Back to Help Home
                  </Link>
                  <a
                    href="mailto:support@gcra.gov.za"
                    style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      textDecoration: 'none'
                    }}
                  >
                    Contact Support
                  </a>
                  <Link
                    to="/help/faq"
                    style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      textDecoration: 'none'
                    }}
                  >
                    FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={{ minHeight: '600px' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpLayout;
